import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
} from "@chakra-ui/react";
import { AlertMessage, MovieCardDetailAction, Paginate } from "@picovico/ui";
import {
  catchErrorHandler,
  convertTimeTo12HourFormat,
  downloadFile,
} from "@picovico/utils";
import { ProfileDrawerData, ProfileStatTabProps } from "./ProfileStatTabs";

import { useAppToast } from "@/hooks/useAppToast";
import { useWebFeature } from "@/hooks/useWebFeature";
import { getPageRoutes } from "@/routes/pageRoutes";
import { useAppSelector } from "@/store";
import { formatShowDateTime, getCinemaHallTimeZone } from "@/utils/showtime";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { useState } from "react";
import { IoFastFoodOutline } from "react-icons/io5";
import { useGenerateTicketMutation } from "store/services/booking";
import { useGetUserPurchaseHistoryQuery } from "store/services/user";
import { UserPurchaseHistoryDTO } from "types/index";

function TicketPurchasedCard({
  ticket,
  handleClick,
  movieCardDetailActionProps,
}: {
  ticket: UserPurchaseHistoryDTO;
  handleClick: (data: ProfileDrawerData) => void;
  movieCardDetailActionProps?: ProfileStatTabProps["movieCardDetailActionProps"];
}) {
  const config = useAppSelector((state) => state.config);
  // The "order food for this ticket" action is gone while web food ordering is off.
  const foodOrderingEnabled = useWebFeature("concession.web_food_ordering");
  const hallTimezone = getCinemaHallTimeZone(
    config.cinema_halls,
    ticket?.cinema_hall,
  );
  const { error: errorToast, success } = useAppToast();
  const [generateTicket, { isLoading }] = useGenerateTicketMutation({});
  const router = useRouter();
  const {
    cardHallScreenTextProps,
    cardDateShowTimeTextProps,
    cardSeatsTextProps,
    cardPaymentStatusBadgeProps,
  } = movieCardDetailActionProps || {};

  return (
    <MovieCardDetailAction
      {...(movieCardDetailActionProps || {})}
      imageProps={movieCardDetailActionProps?.imageProps}
      cardBodyProps={movieCardDetailActionProps?.cardBodyProps}
      onClick={() =>
        handleClick({
          image: ticket?.poster || "",
          data: {
            movie: ticket?.movie,
            cinema_hall: ticket?.cinema_hall,
            screen: ticket?.screen,
            ticket_code: ticket?.ticket_code,
            show_date:
              ticket?.show_date_time &&
              format(new Date(ticket.show_date_time), "PP"),
            show_time:
              ticket?.show_date_time &&
              (formatShowDateTime(
                ticket.show_date_time,
                hallTimezone,
                "h:mm a",
              ) ??
                convertTimeTo12HourFormat(
                  format(new Date(ticket?.show_date_time), "HH:mm"),
                )),
            seats: ticket?.seats,
            payment_status: (
              <Badge colorScheme="success">
                {ticket?.payment_status || ""}
              </Badge>
            ),
            transaction_code: ticket?.transaction_code || "",
            payment_date: ticket?.payment_date
              ? format(new Date(ticket.payment_date), "PPpp")
              : "",
            payment_gateway: ticket?.payment_gateway || "",
            order: ticket?.order || [],
          },
          actionButtons: (
            <Button
              w="full"
              variant="outline"
              color="primary.500"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={() => {
                generateTicket({
                  transaction_code: ticket.transaction_code as string,
                })
                  .unwrap()
                  .then((res: any) => {
                    if ("data" in res && res.data instanceof Blob)
                      downloadFile(res.data, `${ticket.movie}-ticket.pdf`);
                    success("Ticket Downloaded Successfully");
                  })
                  .catch((err) => {
                    catchErrorHandler(err, errorToast);
                  });
              }}
            >
              Download Ticket
            </Button>
          ),
        })
      }
      title={ticket?.movie}
      actionButton={
        <Button
          isLoading={isLoading}
          size="sm"
          variant="outline"
          color={"primary.500"}
          onClick={(e) => {
            e.stopPropagation();
            generateTicket({
              transaction_code: ticket?.transaction_code,
            })
              .unwrap()
              .then((res: any) => {
                if ("data" in res && res.data instanceof Blob)
                  downloadFile(res.data, `${ticket.movie}-ticket.pdf`);
                success("Ticket Downloaded Successfully");
              })
              .catch((err) => {
                catchErrorHandler(err, errorToast);
              });
          }}
        >
          Download Ticket
        </Button>
      }
      detail={
        <Box fontSize="sm">
          <Text
            textTransform="capitalize"
            color="surface.100"
            {...cardHallScreenTextProps}
          >
            {ticket?.cinema_hall}, {ticket?.screen}
          </Text>
          <Text
            textTransform="capitalize"
            color="surface.100"
            {...cardDateShowTimeTextProps}
          >
            {ticket?.show_date_time &&
              format(new Date(ticket?.show_date_time), "PP")}
            |{" "}
            {formatShowDateTime(
              ticket.show_date_time,
              hallTimezone,
              "h:mm a",
            ) ??
              convertTimeTo12HourFormat(
                format(new Date(ticket.show_date_time), "HH:mm"),
              )}
          </Text>
          <Text
            textTransform="capitalize"
            color="surface.100"
            {...cardSeatsTextProps}
          >
            {ticket?.seats}
          </Text>
          <Text
            textTransform="capitalize"
            color="surface.100"
            {...cardSeatsTextProps}
          >
            TC:{ticket?.ticket_code}
          </Text>
          <Flex justifyContent="space-between" alignItems="center" py={1}>
            <Badge colorScheme="success" {...cardPaymentStatusBadgeProps}>
              {ticket?.payment_status}
            </Badge>
            {config?.food_config?.allow_food && foodOrderingEnabled ? (
              ticket?.order?.length > 0 ? (
                <Box color="success.700">
                  <IoFastFoodOutline size={"25px"} color="success.700" />
                </Box>
              ) : (
                <Button
                  size="xs"
                  variant="none"
                  color="primary.500"
                  fontSize={"25px"}
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(
                      getPageRoutes(
                        "food",
                        {},
                        { ticket_code: ticket?.ticket_code },
                      ),
                    );
                  }}
                >
                  <IoFastFoodOutline size={"25px"} color="primary.500" />+
                </Button>
              )
            ) : (
              <></>
            )}
          </Flex>
        </Box>
      }
      image={ticket?.poster || ""}
    />
  );
}

function TicketPurchased({
  openDetail,
  movieCardDetailActionProps,
  alertMessageProps,
}: ProfileStatTabProps) {
  const [page, setPage] = useState(1);
  const { data } = useGetUserPurchaseHistoryQuery(
    {
      search: "current",
      page,
    },
    { refetchOnMountOrArgChange: true },
  );

  return (
    <Box
      sx={{
        h: "full",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {data?.data?.records?.length === 0 && (
        <AlertMessage message="No tickets found" {...alertMessageProps} />
      )}
      <Grid
        flex={1}
        gap="1rem"
        templateColumns={{
          base: "repeat(1, 1fr)",
          sm: "repeat(1, 1fr)",
          md: "repeat(1, 1fr)",
          lg: "repeat(2, 1fr)",
          xl: "repeat(3, 1fr)",
        }}
        overflowX={"auto"}
      >
        {data?.data?.records?.map((ticketData, index) => (
          <GridItem key={`paid_seat__${index}`} colSpan={1}>
            <TicketPurchasedCard
              ticket={ticketData}
              handleClick={openDetail}
              movieCardDetailActionProps={movieCardDetailActionProps}
            />
          </GridItem>
        ))}
      </Grid>
      {data?.data?.total_pages && (
        <Stack direction="row" justify="flex-end" spacing="1rem" mt="1rem">
          <Paginate
            onPageChange={(p) => setPage(p)}
            pageCount={data?.data?.total_pages}
          />
        </Stack>
      )}
    </Box>
  );
}

export default TicketPurchased;
